import { quat, v2, v3 } from "./short3";
import * as Maths3 from "./Maths3";
import * as Commons from "oc-commons/Commons";
import { ThreeBSP } from "./ThreeBSP";
import { Geometry } from "oc-threejs/geometries/Geometry";

import _ from "lodash";
import { OBB } from "three/examples/jsm/math/OBB";
import { Cylinder } from "./math/Cylinder";
import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  DirectionalLight,
  DoubleSide,
  EdgesGeometry,
  Light,
  Line,
  Line3,
  LineBasicMaterial,
  LineBasicMaterialParameters,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshPhongMaterial,
  MeshPhongMaterialParameters,
  Object3D,
  Plane,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  Scene,
  Sphere,
  SphereGeometry,
  Sprite,
  Vector2,
  Vector3,
} from "three";

declare const STAGE: any;

// Interface for Cone (since Cone.js is not yet migrated)
export interface ICone {
  height: number;
  radiusBottom(): number;
  direction: Vector3;
  center(): Vector3;
  clone(): ICone;
  setHeight(h: number): ICone;
}

// statische Hilfsvariablen, die in einigen Methoden benutzt werden
const edge = new Vector3();
const normal = new Vector3();
const EPSILON = 0.000001;
const matrixHelper = new Matrix4(); // Renamed to avoid collision with export const matrix

/**
 * Statische Utility-Klasse zum Abfragen und Manipulieren der Scene und Objektgeometrien
 *
 * Weitere Hilfsroutinen finden sich in three.convenience.js, wo diverese Threejs-Objekte monkeypatched werden
 */

// ---------------------------------------------------------------------------------
// Objekte Löschen
// ---------------------------------------------------------------------------------

/**
 * Löscht ein Object von seinem Parent, falls Object und Parent existieren
 */
export function remove(object: Object3D | null | undefined): Object3D | null | undefined {
  if (object && object.parent) {
    object.parent.remove(object);
  }
  return object;
}

/**
 * Löscht alle Children eines Objectes
 */
export function empty(object: Object3D): Object3D {
  const children = object?.children;
  if (children == null) {
    return object;
  }
  _.clone(children).forEach((o) => object.remove(o));
  return object;
}

/**
 * Entfernt alle Lichtquellen der Scene
 */
export function removeLights(): void {
  const lights = filter(STAGE.scene, (x) => x instanceof Light);
  lights.forEach((o) => remove(o));
}

// ---------------------------------------------------------------------------------
// Traversierung / Filter / Map
// ---------------------------------------------------------------------------------

/**
 * Gibt alle Objekte des Baums zurück, für die predicate(object)==true ist (depth-first).
 */
export function filter(
  object: Object3D,
  predicate: (o: Object3D) => boolean = () => true,
): Object3D[] {
  const list: Object3D[] = [];
  object.traverse((node) => {
    if (predicate(node)) {
      list.push(node);
    }
  });
  return list;
}

/**
 * Wie filter, aber im Gegensatz dazu muss auch für die besuchten Parents eines Objektes predicate true sein.
 */
export function filterWhile(
  object: Object3D,
  predicate: (o: Object3D) => boolean = (object) => true,
): Object3D[] {
  const list: Object3D[] = [];
  traverseWhile(object, predicate, (o) => {
    list.push(o);
  });
  return list;
}

/**
 * Mappt alle Objekte des Baums
 */
export function map<T>(object: Object3D, mapper: (o: Object3D) => T): T[] {
  const list: T[] = [];
  object.traverse((node) => {
    const o = mapper(node);
    if (o) {
      list.push(o);
    }
  });
  return list;
}

/**
 * Findet ein Objekt oder einen Punkt anhand eines SelectorStrings. Dieser kann sein:
 *   1. ein Punkt wie: 0,0.5,-1.2
 *   2. der Name eines Objektes wie: Schrank
 *   3. ein zu evaluierender Pfadausdruck wie find("Schrank").nthChild(2).filter("Schublade").get(3).parent().grid(0,0,1).add(0,0,1) oder cameraTarget() oder cameraPosition()
 */
export function findBySelector(object: Object3D, selectorString: string): any {
  if (
    /^(?:\s*[-]?\d+(?:\.\d+)?\s*,\s*[-]?\d+(?:\.\d+)?\s*,\s*[-]?\d+(?:\.\d+)?\s*)?$/g.test(
      selectorString,
    )
  ) {
    // 1. ein Punkt wie 0,0.5,-1.2
    const p = selectorString.split(/\s*,\s*/);
    const o = v3().fromArray(p.map(parseFloat));
    return o;
  } else if (/(parent|filter|find|nthChild)\("/.test(selectorString)) {
    // 3. Ein zu evaluierender Pfadausdruck wie find("Schrank").parent().filter("Schublade")[3].nthChild(1).grid(0,0,1).add(0,0,1)
    const selector = (obj: any) => {
      const wrap = (callback: any) =>
        obj == null ? () => selector(null) : (...args: any[]) => selector(callback(...args));
      return {
        parent: wrap(() => obj.parent),
        cameraTarget: wrap(() => STAGE.orbitcontrols.target.clone()),
        cameraPosition: wrap(() => STAGE.camera.position.clone()),
        filter: wrap((name: string) => filter(obj, (x: any) => x.name == name)),
        find: wrap((name: string) => find(obj, (x: any) => x.name == name)),
        get: wrap((n: any) => obj[n]),
        nthChild: wrap((n: number) => obj.children[n]),
        grid: wrap((x: number, y: number, z: number) => {
          const box = boundingBoxLocalMatrix(obj);
          // Assuming box has a grid method (extended Box3?) or we need to implement/cast it.
          // Since we are migrating Scenes.ts and not Box3 extensions yet (or maybe missed it),
          // let's assume valid JS logic. If Box3 doesn't have grid, this will fail at runtime as before.
          // Wait, Box3 extensions might supply this.
          return (box as any).grid(x, y, z);
        }),
        add: wrap((x: number, y: number, z: number) =>
          (obj instanceof Vector3 ? obj : obj.position).clone().add(v3(x, y, z)),
        ),
        object: obj,
      };
    };
    const selectorFunction = new Function(
      "selector",
      "with (selector) { return " + selectorString + "; }",
    );
    try {
      return selectorFunction(selector(object)).object;
    } catch (e) {
      console.error(
        "findBySelector error on ",
        object,
        " evaluating ",
        selectorString,
        " error is ",
        e,
      );
      return null;
    }
  } else {
    // 2. der Name eines Objektes
    return find(object, (x) => x.name == selectorString);
  }
}

/**
 * Gibt das erste Objekt des Baums zurück, für das predicate(object)==true ist (depth-first)
 */
export function find(object: Object3D, predicate: (o: Object3D) => boolean): Object3D | null {
  return down(object);
  function down(node: Object3D): Object3D | null {
    if (!node) {
      return null;
    }
    if (predicate(node)) {
      return node;
    }
    if (node.children) {
      for (let i = 0, l = node.children.length; i < l; i++) {
        const result = down(node.children[i]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }
}

/**
 * Iteriert im Baum so lange nach oben (parent oder das Objekt selbst), bis predicate(object)==true ist.
 */
export function findUp(
  object: Object3D | null,
  predicateFn: (o: Object3D) => boolean,
): Object3D | null {
  while (
    object != undefined &&
    object !== null &&
    (object as any) !== window &&
    !predicateFn(object)
  ) {
    object = object.parent;
  }
  if ((object as any) === window) {
    return null;
  }
  return object;
}

/**
 * gibt die Scene zurück, in der ein Objekt ist
 */
export function getScene(object: Object3D): Scene | null {
  return findUp(object, (o) => (o as any).isScene) as Scene | null;
}

/**
 * gibt alle Parents des Objektes einschließlich sich selbst als Set zurück
 */
export function getSelfAndAncestorSet(object: Object3D | null): Set<Object3D> {
  const set = new Set<Object3D>();
  while (object != undefined && object !== null && (object as any) !== window) {
    set.add(object);
    object = object.parent;
  }
  return set;
}

/**
 * Prüft, ob possibleChild ein direktes oder indirektes Kind von parent ist.
 */
export function isAnchestorOf(parent: Object3D, possibleChild: Object3D | null): boolean {
  let o = possibleChild;
  while (o != null && (o as any) !== window) {
    o = o.parent;
    if (o === parent) {
      return true;
    }
  }
  return false;
}

/**
 * iteriert über alle Vertices des Objectes und aller Kinder und ruft jeweils iterator(vertex) auf.
 * ACHTUNG! Es handelt sich dabei immer um dieselbe Vertex-Instanz, nur mit anderen Werten! Will man
 * die Vertices speichern, sollte man sie clonen!
 */
export function traverseVertices(
  object: Object3D,
  iterator: (
    vertex: Vector3,
    index: number,
    geometry: Geometry | BufferGeometry,
    node: Object3D,
  ) => void,
): void {
  const v1 = v3();
  object.updateMatrixWorld(true);
  object.traverse(function (node) {
    if (node instanceof Sprite) {
      return;
    }
    const geometry = (node as any).geometry;
    if (geometry !== undefined) {
      if (geometry instanceof Geometry) {
        const vertices = geometry.vertices;
        for (let i = 0, il = vertices.length; i < il; i++) {
          v1.copy(vertices[i]);
          v1.applyMatrix4(node.matrixWorld);
          iterator(v1, i, geometry, node);
        }
      } else if (
        geometry instanceof BufferGeometry &&
        geometry.attributes["position"] !== undefined
      ) {
        const positions = geometry.attributes["position"].array;
        for (let i = 0, il = positions.length; i < il; i += 3) {
          v1.set(positions[i], positions[i + 1], positions[i + 2]);
          v1.applyMatrix4(node.matrixWorld);
          iterator(v1, i, geometry, node);
        }
      }
    }
  });
}

/**
 * iteriert über alle Vertices des Objectes und aller Kinder und ruft jeweils iterator(vertex) auf.
 * ACHTUNG! Ist bei BufferGeometry sehr inneffizient, weil erst eine Geometry daraus erzeugt wird.
 */
export function traverseFaces(
  object: Object3D,
  iterator: (face: any, index: number, geometry: Geometry, node: Object3D) => void,
): void {
  object.updateMatrixWorld(true);
  object.traverse((node) => {
    if (node instanceof Sprite) {
      return;
    }
    let geometry = (node as any).geometry;
    if (geometry == null) {
      return;
    }
    if (geometry instanceof BufferGeometry) {
      geometry = new Geometry().fromBufferGeometry(geometry);
    }
    if (geometry instanceof Geometry) {
      geometry.faces.forEach((face, index) => iterator(face, index, geometry, node));
    }
  });
}

/**
 * iteriert über alle Faces des Objectes und aller Kinder und ruft jeweils iterator(a,b,c,index,geometry,node) auf,
 * wobei a,b,c die drei Ecken des Face sind.
 * ACHTUNG! Es handelt sich dabei immer um dieselbe Vertex-Instanz, nur mit anderen Werten! Will man
 * die Vertices speichern, sollte man sie clonen! */
export function traverseFacesFast(
  object: Object3D,
  iterator: (
    a: Vector3,
    b: Vector3,
    c: Vector3,
    index: number,
    geometry: BufferGeometry | Geometry,
    node: Object3D,
  ) => void,
): void {
  object.updateMatrixWorld(true);
  object.traverse((node) => {
    if (node instanceof Sprite) {
      return;
    }
    const geometry = (node as any).geometry;
    if (geometry !== undefined) {
      const positions = geometry?.attributes?.["position"];
      if (positions !== undefined) {
        const a = [v3(), v3(), v3()];
        let faceIndex = 0;
        const addFace = (...is: number[]) => {
          a.forEach((v, i) => {
            v.fromBufferAttribute(positions, is[i]).applyMatrix4(node.matrixWorld);
          });
          iterator(a[0], a[1], a[2], faceIndex, geometry, node);
          faceIndex++;
        };
        const index = geometry.getIndex();
        if (index) {
          for (let i = 0, il = index.count - 3; i <= il; i += 3) {
            addFace(index.getX(i), index.getX(i + 1), index.getX(i + 2));
          }
        } else {
          for (let i = 0, il = positions.count - 9; i <= il; i += 9) {
            addFace(i, i + 1, i + 2);
          }
        }
      }
    }
  });
}

/**
 * iteriert über alle Edges des Objectes und aller Kinder und ruft jeweils iterator(a,b,index,geometry,node) auf,
 * wobei a,b die beiden Ecken der Edge sind.
 * ACHTUNG! Es handelt sich dabei immer um dieselbe Vertex-Instanz, nur mit anderen Werten! Will man
 * die Vertices speichern, sollte man sie clonen! */
export function traverseEdges(
  object: Object3D,
  iterator: (
    a: Vector3,
    b: Vector3,
    index: number,
    geometry: BufferGeometry | Geometry,
    node: Object3D,
  ) => void,
): void {
  let edgeIndex = 0;
  traverseFacesFast(object, (a, b, c, index, geometry, node) => {
    iterator(a, b, edgeIndex++, geometry, node);
    iterator(b, c, edgeIndex++, geometry, node);
    iterator(c, a, edgeIndex++, geometry, node);
  });
}

/**
 * wie traverseVertices, aber als Liste aller Vertices (gecloned). Im Zweifel diese Methode nehmen!
 */
export function getVertices(object: Object3D): Vector3[] {
  const vertices: Vector3[] = [];
  traverseVertices(object, (v) => vertices.push(v.clone()));
  return vertices;
}

/**
 * Sucht zu der Position eines Objektes das mit dem nahesten Position
 */
export function findNearestObjectPosition(
  object: Object3D,
  otherObjects: Object3D[],
): Object3D | undefined {
  return _.head(_.sortBy(otherObjects, (o) => o.position.distanceToSquared(object.position)));
}

/**
 * Traversiert den Objektbaum für alle Objekte, für die predicate true ist. Sobald predicate false ergibt,
 * wird das Objekt und alle seine Unterobjekte nicht besucht.
 * callbackDown wird vor dem Abstieg, und callbackUp nach dem Abstieg aufgerufen.
 */
export function traverseWhile(
  object: Object3D,
  predicate: (object: Object3D) => boolean = (object) => true,
  callbackDown: (object: Object3D) => void = (object) => {},
  callbackUp: (object: Object3D) => void = (object) => {},
): void {
  if (object && predicate(object)) {
    callbackDown(object);
    if (object.children) {
      for (let i = 0; i < object.children.length; i++) {
        traverseWhile(object.children[i], predicate, callbackDown, callbackUp);
      }
    }
    callbackUp(object);
  }
}

/**
 * Traversiert den Objektbaum für alle Objekte, bis predicate true ist, und ruft dann callback auf.
 * Callback wird für alle Objekte des Baums aufgerufen, für die predicate true ergibt.
 * Allerdings stoppt die Traversierung dort, d.h. es werden die also Children der Objekte,
 * die true ergeben, nicht besucht.
 */
export function traverseFilter(
  object: Object3D,
  predicate: (object: Object3D) => boolean = (object) => true,
  callback: (object: Object3D) => void = (object) => {},
): void {
  if (!object) {
    return;
  }
  if (predicate(object)) {
    callback(object);
    return;
  }
  if (object.children) {
    for (let i = 0; i < object.children.length; i++) {
      traverseFilter(object.children[i], predicate, callback);
    }
  }
}

/**
 * gibt alle Objekte der Szene zurück, deren Name gleich name ist. Durchsucht den gesamten Scenen-Baum.
 */
export function getObjectsByNameRecursive(name: string): Object3D[] {
  return filter(STAGE.scene, (o) => o.name == name);
}

/**
 * gibt die gemeinsame Wurzel (Parent) von a und b zurück
 */
export function getFirstCommonAnchestor(a: Object3D, b: Object3D): Object3D | null {
  const otherAncestorSet = getSelfAndAncestorSet(b);
  let curr: Object3D | null = a;
  while (curr) {
    if (otherAncestorSet.has(curr)) {
      return curr;
    }
    curr = curr.parent;
  }
  return null;
}

/**
 * gibt den weitesten an der Wurzel liegenden Parent von a zurück,
 * der nicht auch Parent von b ist.
 */
export function getLastUncommenAnchestor(a: Object3D, b: Object3D): Object3D | null {
  const common = getFirstCommonAnchestor(a, b);
  if (!common) {
    return null;
  }
  const otherAncestorSet = getSelfAndAncestorSet(b);
  let curr = a;
  while (curr.parent && !otherAncestorSet.has(curr.parent)) {
    curr = curr.parent;
  }
  return curr;
}

// ---------------------------------------------------------------------------------
// Convenience-Methoden für die Erzeugung von Standardgeometrien
// ---------------------------------------------------------------------------------

/**
 * erzeugt ein Box-Mesh
 */
export function box(
  size: Vector3 = v3(1 * STAGE.mInUnit, 1 * STAGE.mInUnit, 1 * STAGE.mInUnit),
  material: Material | MeshPhongMaterialParameters = {
    color: 0xa0a0a0,
  },
): Mesh {
  const theMaterial = material instanceof Material ? material : new MeshPhongMaterial(material);

  const geometry = new BoxGeometry(size.x, size.y, size.z);
  const mesh = new Mesh(geometry, theMaterial);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * erzeugt ein Sphere-Mesh
 */
export function sphere(
  radius = 1,
  material: Material | MeshPhongMaterialParameters = {
    color: 0xa0a0a0,
  },
  parts = 16,
): Mesh {
  const theMaterial = material instanceof Material ? material : new MeshPhongMaterial(material);

  const geometry = new SphereGeometry(radius, parts, parts);
  const mesh = new Mesh(geometry, theMaterial);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * erzeugt eine Linie (Array von 2 Punkten oder THREE.Line3)
 */
export function line(
  pointsOrLine3: Vector3[] | Line3,
  material: Material | LineBasicMaterialParameters = {
    color: 0xa0a0a0,
    linewidth: 1,
  },
): Line {
  const theMaterial = material instanceof Material ? material : new LineBasicMaterial(material);

  let points: Vector3[];
  if (pointsOrLine3 instanceof Line3) {
    points = [pointsOrLine3.start, pointsOrLine3.end];
  } else {
    points = pointsOrLine3;
  }

  const l = new Line(new BufferGeometry().setFromPoints(points), theMaterial);
  l.computeLineDistances();
  return l;
}

/**
 * Erzeugt eine Plane (Eingabe ist THREE.Plane)
 */
export function plane(
  plane: Plane,
  size: Vector2 = v2(1 * STAGE.mInUnit, 1 * STAGE.mInUnit),
  material: Material | MeshPhongMaterialParameters = {
    color: 0xa0a0a0,
    side: DoubleSide,
  },
  widthSegments = 1,
  heightSegments = 1,
): Mesh {
  const theMaterial = material instanceof Material ? material : new MeshPhongMaterial(material);

  const geometry = new PlaneGeometry(size.x, size.y, widthSegments, heightSegments);

  const mesh = new Mesh(geometry, theMaterial);
  mesh.quaternion.copy(new Quaternion().setFromUnitVectors(Maths3.vector.z, plane.normal));
  mesh.position.copy(plane.coplanarPoint(mesh.position));
  return mesh;
}

/**
 * Erzeugt einen Cylinder (Eingabe ist oc-threejs/math/Cylinder.js)
 */
export function cylinder(
  cylinder: Cylinder,
  material: Material | MeshPhongMaterialParameters = {
    color: 0xa0a0a0,
  },
): Mesh {
  const theMaterial = material instanceof Material ? material : new MeshPhongMaterial(material);

  const cylinderFinite = Number.isFinite(cylinder.height)
    ? cylinder
    : cylinder.clone().setHeight(100);
  const geometry = new CylinderGeometry(
    cylinderFinite.radius,
    cylinderFinite.radius,
    cylinderFinite.height,
    20,
  );

  const mesh = new Mesh(geometry, theMaterial);
  mesh.quaternion.copy(
    new Quaternion().setFromUnitVectors(Maths3.vector.y, cylinderFinite.direction),
  );
  mesh.position.copy(cylinderFinite.center());
  return mesh;
}

/**
 * Erzeugt einen Cone (Eingabe ist oc-threejs/math/Cone.js)
 */
export function cone(
  cone: ICone,
  material: Material | MeshPhongMaterialParameters = {
    color: 0xa0a0a0,
  },
): Mesh {
  const theMaterial = material instanceof Material ? material : new MeshPhongMaterial(material);

  const coneFinite = Number.isFinite(cone.height) ? cone : cone.clone().setHeight(100);
  const geometry = new CylinderGeometry(coneFinite.radiusBottom(), 0, coneFinite.height, 20);

  const mesh = new Mesh(geometry, theMaterial);
  mesh.quaternion.copy(new Quaternion().setFromUnitVectors(Maths3.vector.y, coneFinite.direction));
  mesh.position.copy(coneFinite.center());
  return mesh;
}

/**
 * erzeugt ein Wireframe zu einem Mesh
 */
export function wireframe(mesh: Mesh, linewidth = 1, color = 0x909090): LineSegments {
  const geometry = new EdgesGeometry(mesh.geometry); // or WireframeGeometry
  const material = new LineBasicMaterial({ color, linewidth });
  const edges = new LineSegments(geometry, material);
  return edges;
}

/**
 * erzeugt ein Wireframe zu einer Box3
 */
export function wireframeBox(
  box: Box3,
  materialParams: LineBasicMaterialParameters | Material = {
    color: 0xa0a0a0,
  },
): LineSegments {
  const size = box.getSize(v3());
  const position = box.getCenter(v3());
  const geometry = new BoxGeometry(size.x, size.y, size.z);
  const edges = new EdgesGeometry(geometry);
  const wireframe = new LineSegments(
    edges,
    (materialParams as any).isMaterial
      ? (materialParams as Material)
      : new LineBasicMaterial(materialParams as LineBasicMaterialParameters),
  );
  wireframe.position.copy(position);
  return wireframe;
}

/**
 * erzeugt einen einzelnen, kleinen Punkt, der aber ein Mesh ist
 */
export function createPointObject(): Line {
  const line = new Line(new BufferGeometry().setFromPoints([v3(0, 0, 0)]), new LineBasicMaterial());
  return line;
}

// ---------------------------------------------------------------------------------
// Größen und Position von Objekten ermitteln
// ---------------------------------------------------------------------------------

/**
 * Convenience Methode zur besseren lesbarkeit des Codes, welche intern boundingBox(..,..,false) aufruft
 * @deprecated besser boundingBoxLocalMatrix verwenden, weil diese Methode bei Unterobjekten die jeweilige Matrix
 *     ignoriert!
 */
export function boundingBoxLocal(
  object: Object3D | Object3D[],
  predicate: (o: Object3D) => boolean = (o) => true,
): Box3 {
  return boundingBox(object, predicate, false);
}

/**
 * gibt die BoundingBox zu einem Object zurück.
 */
export function boundingBox(
  object: Object3D | Object3D[],
  predicate: (o: Object3D) => boolean = (o) => true,
  inWorldSpace = true,
): Box3 {
  if (!inWorldSpace) {
    console.warn(
      "boundingBoxLocal ist deprecated! Besser boundingBoxLocalMatrix verwenden, weil damit die Matrix von Unterobjekten korrekt berücksichtigt wird!",
    );
  }

  if (_.isArray(object)) {
    return object
      .map((o) => boundingBox(o, predicate, inWorldSpace))
      .reduce((a, b) => a.union(b), new Box3());
  }
  // Code von THREE.Box3().setFromObject kopiert und erweitert, so dass nur solche Objekte
  // zur BoundingBox zählen, bei denen predicate true ergibt
  const box = new Box3();
  const v1 = new Vector3();
  if (inWorldSpace) {
    object.updateMatrixWorld(true);
  }
  box.makeEmpty();
  traverseWhile(object, predicate, (node) => {
    const geometry = (node as any).geometry;
    if (geometry !== undefined) {
      if (geometry instanceof Geometry) {
        const vertices = geometry.vertices;
        for (let i = 0, il = vertices.length; i < il; i++) {
          v1.copy(vertices[i]);
          if (inWorldSpace) {
            v1.applyMatrix4(node.matrixWorld);
          }
          box.expandByPoint(v1);
        }
      } else if (geometry instanceof BufferGeometry) {
        const attribute = geometry.attributes.position;
        if (attribute !== undefined) {
          for (let i = 0, l = attribute.count; i < l; i++) {
            v1.fromBufferAttribute(attribute, i);
            if (inWorldSpace) {
              v1.applyMatrix4(node.matrixWorld);
            }
            box.expandByPoint(v1);
          }
        }
      }
    }
  });
  return box;
}

/**
 * gibt die BoundingBox zu einem Object zurück.
 */
export function boundingBoxLocalMatrix(
  object: Object3D,
  predicate: (o: Object3D) => boolean = (o) => true,
  parentMatrix: Matrix4 = new Matrix4(),
  ignoreOwnMatrix = false,
): Box3 {
  // Code von THREE.Box3().setFromObject kopiert und erweitert, so dass nur solche Objekte
  // zur BoundingBox zählen, bei denen predicate true ergibt
  const box = new Box3();
  const v1 = new Vector3();
  box.makeEmpty();

  const traverse = (parentMatrix: Matrix4, object: Object3D, useMatrix: boolean) => {
    if (!predicate(object)) {
      return;
    }
    object.updateMatrix();
    const matrix = useMatrix ? parentMatrix.clone().multiply(object.matrix) : parentMatrix;
    const geometry = (object as any).geometry;
    if (geometry !== undefined) {
      if (geometry instanceof Geometry) {
        const vertices = geometry.vertices;
        for (let i = 0, il = vertices.length; i < il; i++) {
          v1.copy(vertices[i]);
          v1.applyMatrix4(matrix);
          box.expandByPoint(v1);
        }
      } else if (geometry instanceof BufferGeometry) {
        const attribute = geometry.attributes.position;
        if (attribute !== undefined) {
          for (let i = 0, l = attribute.count; i < l; i++) {
            v1.fromBufferAttribute(attribute, i);
            v1.applyMatrix4(matrix);
            box.expandByPoint(v1);
          }
        }
      }
    }
    object.children.forEach((child) => traverse(matrix, child, true));
  };

  traverse(parentMatrix, object, !ignoreOwnMatrix);

  return box;
}

/**
 * gibt die Größe der BoundingBox eines Objectes zurück
 */
export function size(object: Object3D): Vector3 {
  return boundingBox(object).getSize(v3());
}

/**
 * gibt die BoundingSphere zurück
 */
export function boundingSphere(objectsOrSphere: Object3D | Object3D[]): Sphere {
  return boundingBox(objectsOrSphere).getBoundingSphere(new Sphere());
}

/**
 * gibt die gemeinsame BoundingBox für ein array von Objecten zurück
 */
export function unionBounds(objects: Object3D[]): Box3 {
  const result = boundingBox(objects[0]);
  for (let i = 1; i < objects.length; i++) {
    result.union(boundingBox(objects[i]));
  }
  return result;
}

/**
 * prüft, ob ein Object3D leer ist, also (selbst oder seine Kinder) keine Geometry enthält
 */
export function isEmpty(object: Object3D): boolean {
  return find(object, (o) => (o as any).geometry != null) == null;
}

// ---------------------------------------------------------------------------------
// Positionierung und Orientierung
// ---------------------------------------------------------------------------------

/**
 * zentriert ein Objekt im Nullpunkt (anhand seiner BoundingBox)
 */
export function center(object: Object3D): Object3D {
  object.position.set(0, 0, 0);
  const b = boundingBox(object);
  Maths3.addPosition(
    object,
    -(b.max.x + b.min.x) / 2,
    -(b.max.y + b.min.y) / 2,
    -(b.max.z + b.min.z) / 2,
  );
  return object;
}

/**
 * setzt ein Objekt mit der Unterkante seiner BoundingBox genau auf den Boden (die y-Ebene durch den Nullpunkt)
 */
export function onGround(object: Object3D): Object3D {
  const b = boundingBox(object);
  Maths3.addPosition(object, 0, -b.min.y, 0);
  return object;
}

/**
 * gibt die "Blickrichtung" eines Objektes zurück (z-Vector seines lokalen Koordinatensystems)
 */
export function getOrientationVector(object: Object3D): Vector3 {
  matrixHelper.extractRotation(object.matrixWorld);
  return v3(0, 0, 1).applyMatrix4(matrixHelper);
}

/**
 * Orientiert ein Objekt so, dass es in die Kamerarichtung schaut
 */
export function orientateInCameraDirection(
  object: Object3D,
  options: { plane?: Plane; halfSphere?: boolean } = {},
): void {
  const plane = options.plane || null;
  const halfSphere = options.halfSphere || false;

  const camDir = STAGE.camera.getWorldDirection(v3());
  const targetDir = plane ? plane.projectPoint(camDir, v3()).normalize() : camDir;

  const q = quat().setFromUnitVectors(Maths3.vector.z, targetDir);

  if (halfSphere) {
    const objectDirection = getOrientationVector(object);
    const sign = Math.sign(targetDir.dot(objectDirection)) < 0 ? -1 : 1;
    if (sign < 0) {
      q.multiply(Maths3.quaternion.y180);
    }
  }

  object.quaternion.copy(q);
}

/**
 * Definiert einen Pivot-Punkt, also den zentralen Drehpunkt eines Objektes.
 * Der Pivot-Punkt wird relativ zum Nullpunkt des übergebenen Objektes angegeben.
 * Der Pivot-Punkt wird als Objekt3D erzeugt und zurückgegeben.
 * Das übergegebene Objekt wird als Child zum Pivot-Objekt hinzugefügt.
 */
export function pivot(object: Object3D, point: Vector3): Object3D {
  const pivot = new Object3D();
  pivot.add(object);
  pivot.position.add(point);
  object.position.sub(point);
  return pivot;
}

export interface BringOptions {
  what: any[]; // THREE.Vector3 | THREE.Quaternion | THREE.Matrix4 | THREE.Object3D | Virtual3D
  to: any[];
  rotate?: boolean;
  translate?: boolean;
  slerpFactor?: number;
}

/**
 * Rotiert oder verschiebt ein Objekt, so dass eins seiner Kindobjekte eine bestimmte Position oder Ausrichtung
 * relativ zu einem anderen Objekt einnimmt.
 */
export function bring({
  what,
  to,
  rotate = false,
  translate = false,
  slerpFactor = 1,
}: BringOptions): void {
  const check = (array: any[], name: string) => {
    if (array.length == 0) {
      throw `'${name}' may not be empty`;
    }
    array.forEach((o) => {
      if (o == null) {
        throw `'${name}' may not contain null values`;
      }
      if (
        !(
          o instanceof Vector3 ||
          o instanceof Quaternion ||
          o instanceof Matrix4 ||
          (o as any).matrix !== undefined ||
          (o as any).matrixWorld !== undefined
        )
      ) {
        throw `'${name}' must only contain Vector3, Quaternion, Matrix4 or an Object with a matrix`;
      }
    });
  };

  const multiply = (matrixes: Matrix4[]) =>
    matrixes.reduce((acc, cur) => acc.multiply(cur), new Matrix4());

  const toMatrix = (a: any): Matrix4 => {
    if (a.matrix) {
      return a.matrix;
    } else if (a instanceof Matrix4) {
      return a;
    } else if (a instanceof Quaternion) {
      return new Matrix4().makeRotationFromQuaternion(a);
    } else if (a instanceof Vector3) {
      return new Matrix4().setPosition(a);
    } else {
      throw new Error("unknown matrix transformation for " + a);
    }
  };

  const getWorldMatrixOfArray = (array: any[]): Matrix4 => {
    let isFirst = true;
    let m = new Matrix4();
    for (let a of array) {
      if (isFirst && a.matrixWorld) {
        m.multiply(a.matrixWorld);
      } else {
        m.multiply(toMatrix(a));
      }
      isFirst = false;
    }
    return m;
  };

  const position = (matrix: Matrix4) => v3().setFromMatrixPosition(matrix);
  const quaternion = (matrix: Matrix4) => matrix.getRotation();
  const inverse = (matrix: Matrix4) => new Matrix4().copy(matrix).invert();

  check(what, "what");
  check(to, "to");

  const oWhatFirst = _.first(what);
  if (oWhatFirst.matrix == undefined && oWhatFirst.matrixWorld == undefined) {
    throw `the first element of 'what' must be an Object with a matrix`;
  }

  const updateMatrixWorld = (o: any) => {
    if (o.updateWorldMatrix) {
      o.updateWorldMatrix(true, true);
    }
  };

  updateMatrixWorld(oWhatFirst);

  if (rotate) {
    const whatLast = _.last(what);
    const toLast = _.last(to);
    const whatLastIsVector = whatLast instanceof Vector3;
    const toLastIsVector = toLast instanceof Vector3;

    if (whatLastIsVector || toLastIsVector) {
      if (whatLastIsVector != toLastIsVector) {
        throw new Error("a vector must be aligned to another vector");
      }
      // die beiden letzten Elemente der what und to Arrays sind Richtungsvektoren und definieren
      // jeweils eine Achse. Diese beiden Achsen sollen im Welt-Koordinatensystem parallel zueinander ausgerichtet sein.
      // Die Rotationskomponente von what um diese Achse herum wird nicht verändert.
      what = what.slice(0, -1);
      to = to.slice(0, -1);
      const wmWhat = getWorldMatrixOfArray(what);
      const wmTo = getWorldMatrixOfArray(to);
      const wmiWhatFirst = inverse(oWhatFirst.matrixWorld);
      const wpsWhat = [v3(), whatLast.clone()].map((v) =>
        v.applyMatrix4(wmWhat).applyMatrix4(wmiWhatFirst),
      );
      const wpsTo = [v3(), toLast.clone()].map((v) =>
        v.applyMatrix4(wmTo).applyMatrix4(wmiWhatFirst),
      );
      const luWhat = wpsWhat[1].sub(wpsWhat[0]).normalize();
      const luTo = wpsTo[1].sub(wpsTo[0]).normalize();
      const lqDelta = new Quaternion().setFromUnitVectors(luWhat, luTo);
      const lqDeltaSlerped = new Quaternion().slerp(lqDelta, slerpFactor);
      oWhatFirst.quaternion.multiply(lqDeltaSlerped);
      updateMatrixWorld(oWhatFirst);
    } else {
      // die World-Quaternion von what und to sollen gleich sein (d.h. die Orientierung soll in allen Achsen gleich sein)
      // seit 9.4.18 geht's auch für den Fall, dass der Parent relativ zur Szene gedreht ist

      const backMatrix = multiply(what.map(toMatrix).slice(1).reverse().map(inverse));
      const wqTo = quaternion(getWorldMatrixOfArray(to).multiply(backMatrix));
      let lqTo = null;
      if (oWhatFirst.parent) {
        const wqParent = quaternion(oWhatFirst.parent.matrixWorld);
        lqTo = wqParent.getRotationFromMeTo(wqTo); // Assuming extended Quaternion
      } else {
        lqTo = wqTo;
      }
      oWhatFirst.quaternion.slerp(lqTo, slerpFactor);
      updateMatrixWorld(oWhatFirst);
    }
  }

  if (translate) {
    // die World-Position von what und to soll gleich sein
    // Matrix: A*B=C => B=I(A)*C
    // I(A*B)=I(B)*I(A)
    const wpWhat = position(getWorldMatrixOfArray(what));
    const wpTo = position(getWorldMatrixOfArray(to));
    const wpDelta = wpTo.sub(wpWhat).multiplyScalar(slerpFactor);
    if (oWhatFirst?.parent?.matrixWorld && !rotate) {
      const wqiParent = quaternion(oWhatFirst.parent.matrixWorld).invert();
      wpDelta.applyQuaternion(wqiParent);
    }

    if (oWhatFirst?.parent?.matrixWorld) {
      const worldScaleParent = v3().setFromMatrixScale(oWhatFirst.parent.matrixWorld);
      wpDelta.divide(worldScaleParent);
    }
    oWhatFirst.position.add(wpDelta);
    updateMatrixWorld(oWhatFirst);
  }
}

/* ---------------------------------------------------------------------------------
/* CSG-Operationen (Boolsche Operationen auf Meshs)
/* ---------------------------------------------------------------------------------
 *  Allgemeine Hinweise:
 *  1. ThreeBSP cached die bsp-Daten am Objekt. Sollte die Geometry eines Objekts upgedated werden,
 *      so müssen also die bsp-Daten gelöscht werden (delete jaco3d.bsp)
 *  2. Die Operationen können erst am Ende von jaco3d.update3d ausgeführt werden (nach der Erstellung der Geometry)
 */

/**
 * Vereint die Meshs der beiden Object3D a und b und übernimmt das Ergebnis in die Geometry von a
 */
export function union(a: Object3D, b: Object3D): void {
  _boolean("union", a, b);
}

/**
 * Subtrahiert vom Mesh des Object3D a das Mesh des Object3D b und übernimmt das Ergebnis in die Geometry von a
 */
export function subtract(a: Object3D, b: Object3D): void {
  _boolean("subtract", a, b);
}

/**
 * Bildet die Schnittmenge der beiden Object3D a und b und übernimmt das Ergebnis in die Geometry von a
 */
export function intersect(a: Object3D, b: Object3D): void {
  _boolean("intersect", a, b);
}

/**
 * @param {string} operation
 * @param {THREE.Object3D} a
 * @param {THREE.Object3D} b
 */
export function _boolean(
  operation: "union" | "subtract" | "intersect",
  a: Object3D,
  b: Object3D,
): void {
  if (STAGE.settings.supress_boolean_operations_for_debugging) {
    return;
  }
  const t0 = performance.now();

  const anchestor = getFirstCommonAnchestor(a, b);
  let aMatrix: Matrix4 | null = null;
  let bMatrix: Matrix4 | null = null;
  if (anchestor) {
    const mulMat = (x: Object3D | null, matrix: Matrix4): Matrix4 => {
      if (x && x !== anchestor) {
        mulMat(x.parent, matrix);
        x.updateMatrix();
        matrix.multiply(x.matrix);
      }
      return matrix;
    };
    aMatrix = mulMat(a.parent, new Matrix4());
    bMatrix = mulMat(b.parent, new Matrix4());
    a.updateMatrix();
    b.updateMatrix();
  } else {
    a.updateMatrixWorld(true);
    b.updateMatrixWorld(true);
    aMatrix = null;
    bMatrix = null;
  }

  const absps = ThreeBSP.getBSPs(a, [], aMatrix);
  const bbsps = ThreeBSP.getBSPs(b, [], bMatrix);

  absps.forEach((absp: any) => {
    const mesh = absp.mesh;
    bbsps.forEach((bbsp: any) => {
      if (operation !== "union" && STAGE.settings.support_csg_multi_materials === false) {
        bbsp.setMaterialIndexOnAllPolygons(0);
      }

      absp = absp[operation](bbsp);
    });
    absp.setGeometryInto(mesh);
  });

  const dt = performance.now() - t0;
  if (dt > 300) {
    console.warn(
      "csg operation " +
        operation +
        " takes very long, " +
        dt.toFixed(0) +
        "ms, try to simplify the meshes",
      a,
      b,
    );
  }
}

/**
 * positioniert und orientiert (aber skaliert nicht) ein Mesh entsprechend einer OBB.
 */
export function setUnitBoxMeshFromOBB(bxMesh: Object3D, obb: OBB): void {
  const mat4 = new Matrix4().setFromMatrix3(obb.rotation).setPosition(obb.center);
  const pos = new Vector3();
  const quat = new Quaternion();
  const scl = new Vector3();
  mat4.decompose(pos, quat, scl);
  bxMesh.position.copy(pos);
  bxMesh.quaternion.copy(quat);
}

// ---------------------------------------------------------------------------------
// Objekte gruppieren (zu einem Objekt zusammenfügen und nachher wieder auflösen)
// ---------------------------------------------------------------------------------

/**
 * fügt child als neues Kind zu parent hinzu, ohne die Weltposition von Child zu ändern,
 * und merkt sich, dass das mit dieser Methode erfolgt ist.
 */
export function groupAdd(parent: Object3D, child: Object3D): void {
  const matrixWorldInverse = new Matrix4();
  matrixWorldInverse.copy(parent.matrixWorld).invert();
  child.applyMatrix4(matrixWorldInverse);
  parent.add(child);
  (child as any)._GROUPED_ = true;
}

/**
 * fügt alle Objekte einer Liste als neue Kinder zu parent hinzu, ohne die Weltposition der Kinder zu ändern,
 * und merkt sich, dass das mit dieser Methode erfolgt ist.
 */
export function groupObjects(object: Object3D, list: Object3D[]): void {
  list.forEach((o) => {
    object.attach(o);
    (o as any)._GROUPED_ = true;
  });
}

/**
 * Entfernt alle mit groupAdd/groupObjects zu einem Parent hinzugefügten childs,
 * ohne die Weltposition der Kinder zu ändern, und fügt sie Top-Level der Szene hinzu.
 */
export function unGroupObjects(object: Object3D): void {
  const scene = STAGE.scene;
  if (object.parent) {
    scene.updateMatrixWorld();
    const grouped = object.children.filter((o) => (o as any)._GROUPED_);
    grouped.forEach((o) => {
      delete (o as any)._GROUPED_;
      object.parent?.attach(o); // Original code said object.parent(o) which is likely wrong for standard THREE.JS, it should be attach or add. Assuming attach based on groupObjects using attach.
      // JS code: object.parent(o); -> This looks suspicious. Maybe object.parent.add(o) or attach?
      // Re-reading JS: object.parent(o) -> object.parent is a property, not a function.
      // Wait, original JS code:
      /*
        if (object.parent) {
            scene.updateMatrixWorld();
            const grouped = object.children.filter((o) => o._GROUPED_);
            grouped.forEach((o) => {
            delete o._GROUPED_;
            object.parent(o); // This lines seems to be a TYPO in original JS or custom method?
            // "object.parent" is a property. Call it?
            // Maybe it meant object.parent.add(o) but parent is an Object3D.
            // Or maybe it meant STAGE.scene.attach(o)? The comment says "fügt sie Top-Level der Szene hinzu".
            // So it should be scene.attach(o).
            });
        }
       */
      scene.attach(o); // Fixing suspected bug/typo in original JS to match comment "Top-Level der Szene"
    });
  }
}

// ---------------------------------------------------------------------------------
// Sonstiges
// ---------------------------------------------------------------------------------

/**
 * Gibt true zurück, wenn das Objekt mit der Stage verbunden ist (Gegenteil von isOrphan)
 */
export function isInScene(object: Object3D): boolean {
  return !isOrphan(object);
}

/**
 * Gibt true zurück, wenn das Objekt nicht mit der Stage verbunden ist.
 */
export function isOrphan(object: Object3D | null): boolean {
  if (object == null) {
    return false;
  }
  do {
    object = object.parent;
    if (object == null) {
      return true;
    }
    if (object instanceof Scene) {
      return false;
    }
  } while (true);
}

/**
 * Gibt eine Funktion zurück, die maximal einmal pro Rendering aufgerufen wird.
 */
export function cachePerFrame<T>(resultFn: (obj: any) => T, objectFn: () => any): () => T | null {
  let result: T | null = null;
  let heartbeat = Number.NaN;
  return () => {
    const object = objectFn();
    if (heartbeat != object.__HEARTBEAT__) {
      heartbeat = object.__HEARTBEAT__;
      result = resultFn(object);
    }
    return result;
  };
}

/**
 * Iteriert Punkte in einem Raster in einem Kreis um einen Strahl.
 */
export function iteratePointsOnCircleRaster(
  ray: { origin: Vector3; direction: Vector3 },
  radius: number,
  numberOfPoints: number,
  iteratee: (p: Vector3) => boolean | void,
): void {
  const basis = [v3(), v3()];
  Maths3.generateComplementBasis(ray.direction, basis[0], basis[1]);

  const rasterPointsOnSquareSide = Math.max(
    1,
    Math.ceil(Math.sqrt((4 / Math.PI) * numberOfPoints)),
  );
  const rasterLength = (2 * radius) / rasterPointsOnSquareSide;

  const v = (vect: Vector3, scale: number) =>
    vect.clone().multiplyScalar(scale * rasterLength - radius);

  const origin = ray.origin.clone();

  for (let i = 0; i < rasterPointsOnSquareSide; i++) {
    for (let j = 0; j < rasterPointsOnSquareSide; j++) {
      const rasterPoint = v(basis[0], i).add(v(basis[1], j)).add(origin);
      if (rasterPoint.distanceTo(origin) > radius) {
        continue;
      }
      if (iteratee(rasterPoint)) {
        return;
      }
    }
  }
}

/**
 * iteriert auf den Seiten eines regelmäßigen, konvexen 2D-Polygons (wie ein Quadrat) und
 * ruft jeweils ein Callback für jeden Punkt auf
 */
export interface PolygonOptions {
  center?: Vector3;
  sideZeroNormal?: Vector3;
  planeNormal?: Vector3;
  radius?: number;
  numberOfSides?: number;
  positionsPerSide?: (number | string)[];
  filterSideIndizes?: number[];
  innerRadius?: number;
  offset?: number;
  raycastOnModel?: boolean;
  quaternion?: Quaternion;
}

export interface PointsOnSideOfRegularConvexPolygonParameters {
  point: Vector3;
  middle: Vector3;
  edges: Vector3[];
  sideNormal: Vector3;
  sideIndex: number;
  positionValue: number | string;
  options: PolygonOptions;
}

export type PointsOnSideOfRegularConvexPolygonIterator = (
  params: PointsOnSideOfRegularConvexPolygonParameters,
) => void;

export function iteratePointsOnSideOfRegularConvexPolygon(
  options: PolygonOptions,
  iteratee: PointsOnSideOfRegularConvexPolygonIterator,
): void {
  options = Commons.defaultsDeepSafe({}, options, {
    center: Maths3.vector.zero,
    sideZeroNormal: Maths3.vector.z,
    planeNormal: Maths3.vector.y,
    radius: 1,
    numberOfSides: 4,
    positionsPerSide: ["0%"],
  });
  if (!options.filterSideIndizes) {
    options.filterSideIndizes = _.range(options.numberOfSides || 4);
  }
  const center = options.center!;
  const planeNormal = options.planeNormal!;
  const sideZeroNormal = options.sideZeroNormal!;
  const radius = options.radius!;
  const numberOfSides = options.numberOfSides!;

  const plane = new Plane().setFromNormalAndCoplanarPoint(planeNormal, center);
  const sideZeroCoplanarNormal = plane
    .projectPoint(center.clone().add(sideZeroNormal), v3())
    .sub(center)
    .normalize();
  const sideAngle = (2 * Math.PI) / numberOfSides;
  options.filterSideIndizes!.forEach((sideIndex) => {
    const edges = [-sideAngle / 2, +sideAngle / 2].map((a) =>
      sideZeroCoplanarNormal
        .clone()
        .multiplyScalar(radius)
        .applyAxisAngle(planeNormal, a + sideAngle * sideIndex)
        .add(center),
    );
    const middle = edges[0].clone().add(edges[1]).multiplyScalar(0.5);
    const halfLine = edges[1].clone().sub(middle);
    const halfLineLength = halfLine.length();
    const sideNormal = middle.clone().sub(center).normalize();
    options.positionsPerSide!.map((positionValue) => {
      const position =
        typeof positionValue === "string"
          ? // percent value
            (Number.parseFloat(positionValue.trim().replace("%", "")) || 0) / 100 // Fixed rtrim usage
          : // absolute value
            positionValue / halfLineLength;
      const point = halfLine.clone().multiplyScalar(position).add(middle);
      iteratee({
        point,
        middle,
        edges,
        sideNormal,
        sideIndex,
        positionValue,
        options,
      });
    });
  });
}

/**
 * Benutzt iteratePointsOnSideOfRegularConvexPolygon, um eine Objekt-Factory pro Polygonpunkt aufzurufen.
 * Gibt das Array aller erzeugten Objekte zurück. Damit kann man z.B. um ein Haus herum Fenster erzeugen.
 */
export function createObjectsAroundObject(
  object: Object3D,
  options: PolygonOptions,
  objectFactory: (p: PointsOnSideOfRegularConvexPolygonParameters) => Object3D,
): Object3D[] {
  const sphere = boundingSphere(object);
  options = Commons.defaultsDeepSafe({}, options, {
    center: sphere.center,
    radius: sphere.radius,
    innerRadius: null,
    offset: 0,
    raycastOnModel: false,
    quaternion: Maths3.quaternion.zero,
  });

  options.radius =
    options.innerRadius == null
      ? options.radius
      : options.innerRadius / Math.sin((0.5 - 1 / (options.numberOfSides || 4)) * Math.PI);

  if (options.radius === 0) {
    throw new Error("radius may not be 0");
  }
  const result: Object3D[] = [];
  iteratePointsOnSideOfRegularConvexPolygon(options, (p) => {
    const raycaster = new Raycaster();
    raycaster.set(
      p.point.clone().add(p.sideNormal.clone().multiplyScalar(10)),
      p.sideNormal.clone().multiplyScalar(-1),
    );
    const intersection = options.raycastOnModel
      ? _.head(raycaster.intersectObject(object, true))
      : null;

    const normal = intersection ? intersection.face!.normal.clone() : p.sideNormal;
    const point = intersection ? intersection.point : p.point;

    point.add(normal.clone().multiplyScalar(options.offset || 0));

    const child = objectFactory(p);
    child.position.copy(point).sub(child.parent?.getWorldPosition(v3()));
    child.quaternion.setFromUnitVectors(Maths3.vector.z, normal).multiply(options.quaternion!);
    result.push(child);

    if (!(child as any).sideIndex) {
      (child as any).sideIndex = p.sideIndex;
    }
  });
  return result;
}

/**
 * Manchmal werden Meshs mit Faces mit 0 Vertices geliefert. Diese Methode bereinigt das Object um diese Faces.
 */
export function removeZeroFaceOrVerticesChildren(mesh: Object3D): Object3D {
  function removedCauseEmpty(mesh: Object3D, index: number) {
    if (
      mesh instanceof Mesh &&
      mesh.children.length === 0 &&
      ((mesh.geometry instanceof Geometry &&
        (mesh.geometry.faces.length === 0 || mesh.geometry.vertices.length === 0)) ||
        (mesh.geometry instanceof BufferGeometry &&
          mesh.geometry.getAttribute("position").count === 0))
    ) {
      mesh.parent?.children.splice(index, 1);
      return true;
    }
    return false;
  }

  function traverse(mesh: Object3D) {
    for (let i = 0; i < mesh.children.length; i++) {
      const child = mesh.children[i];
      if (removedCauseEmpty(child, i)) {
        i--;
      } else {
        traverse(child);
      }
    }
  }

  traverse(mesh);

  return mesh;
}

/**
 * Klont ein Objekt und seine Materialien
 */
export function cloneWithMaterials(object: Object3D): Object3D {
  let ob = object.clone(true);
  ob.traverse((o: any) => {
    if (!o.material) return;
    if (Array.isArray(o.material)) {
      // Assuming MultiMaterial, which is array in recent THREE
      o.material = o.material.map((m: Material) => deepCloneMaterial(m));
    } else {
      o.material = deepCloneMaterial(o.material);
    }
  });
  return ob;
}

/** */
export function deepCloneMaterial(material: Material): Material {
  let m = material.clone();
  if ((m as any).map) {
    (m as any).map = (m as any).map.clone();
  }
  return m;
}

/**
 */
export function setShadowMapSize(light: Light, resolution: number): void {
  const maxsize = Math.min(resolution, STAGE.renderer.capabilities.maxTextureSize);
  light.shadow!.mapSize.set(maxsize, maxsize);
  if (light.shadow!.map) {
    light.shadow!.map.dispose();
    light.shadow!.map = null;
  }
}

/**
 * Hiermit kann ein Licht optimal für den Schattenwurf innerhalb einer Box eingestellt werden
 */
export function setDirectionalLightShadowToCoverBoundingBox(
  light: DirectionalLight,
  box: Box3,
): void {
  const direction = light.target.position.clone().sub(light.position).normalize();
  const radius = box.getBoundingSphere(new Sphere()).radius;
  const zero = box.getCenter(v3()).setZ(0);
  light.position.copy(zero).add(direction.clone().multiplyScalar(-radius));
  light.target.position.copy(zero);
  const shadowCameraPlane = new Plane().setFromNormalAndCoplanarPoint(direction, light.position);
  const distances = box.getCorners().map((p) => shadowCameraPlane.distanceToPoint(p));
  const camera = light.shadow.camera;
  camera.near = _.min(distances)!;
  camera.far = _.max(distances)!;
  camera.right = radius;
  camera.left = -radius;
  camera.top = radius;
  camera.bottom = -radius;
  camera.updateProjectionMatrix();
}

export const axisNames = ["x", "y", "z"];

/**
 * wie der Name sagt..
 */
export function traverseAndConvertAllGeometryToBufferGeometry(object: Object3D): void {
  object.traverse((o: any) => {
    if (o.geometry && o.geometry instanceof Geometry) {
      o.geometry = o.geometry.toBufferGeometry();
    }
  });
}
